import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message } from "antd";
import { createVendorCategory, createVendorCategoryAlias, getAllVendorCategories, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const VendorCategoryComponent = ({ exception, onClose, onResolved }) => {
    const [vendorCategoryName, setVendorCategoryName] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [vendorCategories, setVendorCategories] = useState([]);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const extractVendorCategoryDetails = () => {
            setVendorCategoryName(exception?.unknown_infomation);
        };

        if (exception && exception.description) {
            extractVendorCategoryDetails();
        }

        const fetchVendorCategories = async () => {
            setLoading(true);
            try {
                const response = await getAllVendorCategories();
                const categoriesData = response?.data?.data || [];
                setVendorCategories(Array.isArray(categoriesData) ? categoriesData : []);
            } catch (error) {
                message.error('Failed to fetch vendor categories');
            } finally {
                setLoading(false);
            }
        };

        fetchVendorCategories();
    }, [exception]);

    const handleMarkAsNew = async () => {
        setLoading(true);
        if (vendorCategoryName) {
            if (exception.status === 'pending') {
                try {
                    const payload = {
                        name: vendorCategoryName,
                        alias_for: 0
                    };
                    
                    await createVendorCategory(payload);
                    await updateException(exception.id);
                    message.success('Vendor category created');
                    try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                    onClose();
                } catch (error) {
                    message.error('Failed to create vendor category');
                } finally {
                    setLoading(false);
                }
            } else {
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } else {
            console.warn("Vendor category name not found.");
            setLoading(false);
            onClose();
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleVendorCategorySelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        setLoading(true);
        if (exception.status === 'pending') {
            try {
                const payload = {
                    name: vendorCategoryName,
                    alias_for: selectedAliasId
                };
                
                await createVendorCategoryAlias(payload);
                await updateException(exception.id);
                message.success('Alias saved');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } catch (error) {
                message.error('Failed to save alias');
            } finally {
                setLoading(false);
            }
        } else {
            try { if (typeof onResolved === 'function') await onResolved(); } catch {}
            onClose();
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    {vendorCategoryName ? `Select an existing vendor category to alias "${vendorCategoryName}" to.` : 'Select an existing vendor category to alias.'}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a vendor category"
                                    onChange={handleVendorCategorySelect}
                                    disabled={loading}
                                    value={selectedAliasId ?? undefined}
                                >
                                    {vendorCategories.map((category) => (
                                        <Option key={category.id} value={category.id}>
                                            {category.name}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default VendorCategoryComponent;
